// Internal Modules
import { getContent } from '@/lib';
import type { ReactNode } from 'react';
import {
  CRS_TAXPAYERID_OPTIONS,
  FPTI_INVALID_VALUE_ERROR,
  FPTI_VALUE_MISSING_ERROR,
  TAX_PAYER_ID_MAPPER,
} from './constants';
import { logCrsErrors } from './fpti';
import type { MappedTerritories } from '@/store/models/api/types';
import type { DefaultInputState } from '@/store';
import { CountryCodes } from '@/generated/graphql';
/**
 * Helper function to get tax payer id options
 */
type CrsTaxPayerOption = {
  text: ReactNode[] | string;
  value: keyof typeof CRS_TAXPAYERID_OPTIONS;
};

const getCrsTaxPayerIdOptions = (
  countryCode: CountryCodes,
): CrsTaxPayerOption[] => {
  const options: CrsTaxPayerOption[] = [
    {
      text: getContent('crs.taxpayerTypes.noTaxPayerID'),
      value: CRS_TAXPAYERID_OPTIONS.NO_TAXPAYER_ID,
    },
  ];

  if (countryCode === CountryCodes.Us) {
    options.unshift({
      text: getContent('crs.taxpayerTypes.SSN'),
      value: CRS_TAXPAYERID_OPTIONS.SSN,
    });
  } else {
    options.unshift({
      text: getContent('crs.taxpayerTypes.TIN'),
      value: CRS_TAXPAYERID_OPTIONS.TIN,
    });
  }

  return options;
};

/**
 * Function used to determine which countries we should
 * display in the country dropdown
 */

// TODO: this type may not depict the whole picture.
// When the main CRS component gets typed, revisit this type
type Country = {
  countryCode: {
    value: string;
  };
  key: string;
};

type GetCountriesListParams = {
  crsTaxDetails: Country[];
  countriesList: MappedTerritories[];
  currentTaxResidencyCountry: string;
  country: CountryCodes;
};

const getCountriesList = ({
  crsTaxDetails,
  countriesList,
  currentTaxResidencyCountry,
  country,
}: GetCountriesListParams): MappedTerritories[] => {
  const hideCountries = crsTaxDetails
    .filter(details => {
      return details.countryCode.value !== '';
    })
    .map(details => {
      return details.countryCode.value;
    });
  hideCountries.push(country);
  return countriesList.filter(countryItem => {
    if (
      hideCountries.includes(countryItem.key as string) &&
      countryItem.key !== currentTaxResidencyCountry
    ) {
      return false;
    }
    return true;
  });
};

/**
 * Function responsible for updating the state in case a field
 * is empty, meanting the user has not selected or added a value
 * to it yet
 */
type UpdateStateIfEmptyParams = {
  content: string;
  fieldNameForLogging: string;
  fieldValues: DefaultInputState;
  isTaxDetailsValid: boolean;
};
const updateStateIfEmpty = ({
  fieldValues,
  fieldNameForLogging,
  content,
  isTaxDetailsValid,
}: UpdateStateIfEmptyParams): [DefaultInputState, boolean] => {
  if (fieldValues.value.trim() === '') {
    fieldValues = {
      ...fieldValues,
      displayError: true,
      error: getContent(content),
    };
    isTaxDetailsValid = false;
    logCrsErrors({
      error: FPTI_VALUE_MISSING_ERROR,
      field: fieldNameForLogging,
    });
    return [fieldValues, isTaxDetailsValid];
  }
  return [fieldValues, isTaxDetailsValid];
};

type TaxPayerId = {
  displayError?: boolean;
  error?: string;
  value: string;
};
type GetTaxPayerIdMaxLengthParams = {
  countryCode: MappedTerritories;
  taxPayerIdType: TaxPayerId;
};

const getTaxPayerIdMaxLength = ({
  countryCode,
  taxPayerIdType,
}: GetTaxPayerIdMaxLengthParams): number | undefined => {
  const { value: countryCodeValue } = countryCode || {};
  const { value: taxPayerValue } = taxPayerIdType || {};
  if (!countryCodeValue || !taxPayerValue) {
    return undefined;
  }
  return TAX_PAYER_ID_MAPPER?.[countryCodeValue]?.[taxPayerValue]?.maxLength;
};

export type CrsNoTaxpayerIdReasons = {
  notIssued: string;
  unableToObtain: string;
  notReceivedYet: string;
  addExplanation: string;
};
const getCrsNoTaxpayerIdReasons = (): CrsNoTaxpayerIdReasons => ({
  notIssued: getContent('crs.reasons.notIssued'),
  unableToObtain: getContent('crs.reasons.unableToObtain'),
  // eslint-disable-next-line sort-keys
  notReceivedYet: getContent('crs.reasons.waitingTIN'),
  // eslint-disable-next-line sort-keys
  addExplanation: getContent('crs.reasons.explainReason'),
});

type CrsNoTaxPayerSelectOptionType = {
  text: string;
  value: string;
};
const getCrsNoTaxpayerIdReasonsSelectOptions =
  (): CrsNoTaxPayerSelectOptionType[] => {
    const reasons = getCrsNoTaxpayerIdReasons();
    const keys: (keyof typeof reasons)[] = Object.keys(
      reasons,
    ) as (keyof typeof reasons)[];
    const result = keys.map(key => ({
      text: reasons[key],
      value: key,
    }));

    return result;
  };

/**
 * Function responsible for updating the state for tax payer
 * id value in case it is invalid based on TAX_PAYER_ID_MAPPER
 * map config
 */

type updateTaxPayerIdValueIfInvalidParams = {
  countryCode: DefaultInputState;
  isTaxDetailsValid: boolean;
  fieldNameForLogging: string;
  taxPayerIdValue: TaxPayerId;
  taxPayerIdType: TaxPayerId;
};
const updateTaxPayerIdValueIfInvalid = ({
  countryCode,
  isTaxDetailsValid,
  fieldNameForLogging,
  taxPayerIdValue,
  taxPayerIdType,
}: updateTaxPayerIdValueIfInvalidParams): [TaxPayerId, boolean] => {
  if (taxPayerIdValue.value.trim() !== '') {
    const taxPayerIdValueMap =
      TAX_PAYER_ID_MAPPER[countryCode.value]?.[taxPayerIdType.value];
    if (
      taxPayerIdValueMap?.regex &&
      !taxPayerIdValueMap.regex.test(taxPayerIdValue.value)
    ) {
      taxPayerIdValue = {
        ...taxPayerIdValue,
        displayError: true,
        error: getContent(taxPayerIdValueMap.errorMessage),
      };

      logCrsErrors({
        error: FPTI_INVALID_VALUE_ERROR,
        field: fieldNameForLogging,
      });

      isTaxDetailsValid = false;
    }
  }
  return [taxPayerIdValue, isTaxDetailsValid];
};

export {
  getCountriesList,
  getCrsNoTaxpayerIdReasons,
  getCrsNoTaxpayerIdReasonsSelectOptions,
  getCrsTaxPayerIdOptions,
  getTaxPayerIdMaxLength,
  updateStateIfEmpty,
  updateTaxPayerIdValueIfInvalid,
};
